import React from 'react';
import { convertTimeWithTimezone } from '@/utils/time';
import { BoxV2 as Box } from 'portal-commons';
import Tooltip from '@material-ui/core/Tooltip';
import { useHistory, useLocation } from 'react-router-dom';
import { getCategoryName, remapTCRStatus } from '../../utils';
import { CampaignListItem } from '@/apis/core/types';
import { DCA_CAMPAIGN_DETAILS_PATH } from '@/constants/paths';
import { makeStyles } from '@material-ui/core/styles';
import { SelectItem } from '@/types';
import TruncatedTableCell from '@/components/TruncatedTableCell';

interface Props {
  campaign: CampaignListItem;
  categoryOptions: SelectItem[];
}

const useStyles = makeStyles((theme) => ({
  customTooltip: {
    '&.MuiTooltip-tooltip': {
      background: '#0091B3',
      color: '#fff',
      boxShadow: theme.shadows[1],
      fontSize: 12,
      borderRadius: '3px',
      maxWidth: 260,
      fontFamily: 'Roboto',
      fontWeight: 400,
      padding: '6px 8px',
      '& .MuiTooltip-arrow': {
        color: '#0091B3',
      },
    },
  },
}));

const CampaignsByDcasDeclinedListingRow: React.FC<Props> = ({
  campaign,
  categoryOptions,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const handleRowClick = () => {
    history.push({
      pathname: `${DCA_CAMPAIGN_DETAILS_PATH.replace(':id', campaign.uid)}`,
      state: {
        goBackPage: `${location.pathname}${location.search ?? ''}`,
      },
    });
  };

  const descriptionText = `${
    !!campaign.rejectionCategoryList?.length
      ? `${getCategoryName(
          campaign.rejectionCategoryList || [],
          categoryOptions
        )}: `
      : ''
  }${campaign.explanation || ''}`;

  const descriptionTooltip = (
    <>
      {!!campaign.rejectionCategoryList?.length && (
        <>
          <Box as="span" sx={{ fontWeight: 800 }}>
            Rejection Category:
          </Box>
          {` ${getCategoryName(
            campaign.rejectionCategoryList || [],
            categoryOptions
          )}`}
          <br />
          <br />
        </>
      )}
      <Box
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 10,
          WebkitBoxOrient: 'vertical',
        }}
      >
        <Box as="span" sx={{ fontWeight: 800 }}>
          Explanation:{' '}
        </Box>
        {campaign.explanation}
      </Box>
    </>
  );

  return (
    <Box
      as="tr"
      data-testid="CampaignsByDcasDeclinedListingRow"
      onClick={handleRowClick}
    >
      <Box as="td" sx={{ width: 95 }} data-testid="campaignUid">
        {campaign.uid}
      </Box>
      <Box as="td" sx={{ width: 76 }} data-testid="brandUid">
        {campaign.brandUid}
      </Box>
      <TruncatedTableCell
        sx={{ width: 127, maxWidth: 150 }}
        data-testid="brandName"
      >
        {campaign.brandName || 'N/A'}
      </TruncatedTableCell>
      <TruncatedTableCell
        sx={{ width: 112, maxWidth: 130 }}
        data-testid="primaryDcaName"
      >
        {campaign.primaryDcaName || 'N/A'}
      </TruncatedTableCell>
      <TruncatedTableCell
        sx={{ width: 112, maxWidth: 130 }}
        data-testid="mnoList"
      >
        {campaign.mnoList && campaign.mnoList.length
          ? campaign.mnoList.map((mno) => mno.name).join(', ')
          : 'N/A'}
      </TruncatedTableCell>
      <TruncatedTableCell
        sx={{ width: 112, maxWidth: 130 }}
        data-testid="usecase"
      >
        {campaign.usecase || 'N/A'}
      </TruncatedTableCell>
      <Box as="td" sx={{ width: 112 }} data-testid="sharedOnDate">
        {convertTimeWithTimezone(campaign.sharedOnDate, 'MM/DD/YYYY')}
      </Box>
      <Box as="td" sx={{ width: 112 }} data-testid="declineDate">
        {convertTimeWithTimezone(campaign?.declineDate, 'MM/DD/YYYY')}
      </Box>
      <Box as="td" sx={{ width: 297, maxWidth: 297 }} data-testid="description">
        <Tooltip
          classes={{ tooltip: classes.customTooltip }}
          title={descriptionTooltip}
          placement="left"
          arrow
        >
          <Box
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
              pointerEvents: descriptionText ? 'auto' : 'none',
            }}
          >
            {descriptionText || 'N/A'}
          </Box>
        </Tooltip>
      </Box>
      <Box as="td" sx={{ width: 112 }} data-testid="status">
        {remapTCRStatus(campaign.status)}
      </Box>
    </Box>
  );
};

export default CampaignsByDcasDeclinedListingRow;
